// Generates a 10-stop accent palette from a single hex color and writes it
// to CSS variables so the Tailwind `accent-*` utilities (defined in
// tailwind.config.js) pick up the tenant's brand at runtime.
//
// We do this via HSL lightness adjustment, not by mixing toward white/black
// — keeps the hue + saturation stable so the result reads as one colour
// family. Numbers loosely mirror Tailwind's own colour-scale ramp.

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (0 <= h && h < 60)        { r = c; g = x; b = 0; }
  else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
  else if (120 <= h && h < 180){ r = 0; g = c; b = x; }
  else if (180 <= h && h < 240){ r = 0; g = x; b = c; }
  else if (240 <= h && h < 300){ r = x; g = 0; b = c; }
  else                          { r = c; g = 0; b = x; }
  const to255 = (v: number) => Math.max(0, Math.min(255, Math.round((v + m) * 255)));
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(to255(r))}${hex(to255(g))}${hex(to255(b))}`;
}

// Lightness targets per stop. These approximate the Tailwind v3 ramp but
// stay in a single hue. The base accentColor lands at the 500 stop.
const STOP_LIGHTNESS = {
  50:  97,
  100: 93,
  200: 86,
  300: 77,
  400: 67,
  500: 55,  // base
  600: 47,
  700: 38,
  800: 30,
  900: 22,
} as const;

export function applyAccentTheme(accentColor: string): void {
  const hsl = hexToHsl(accentColor);
  if (!hsl) return; // bad input — leave defaults in place
  const { h, s } = hsl;
  // Slightly under-saturate the very light stops so they don't shout.
  const root = document.documentElement;
  for (const stop of Object.keys(STOP_LIGHTNESS)) {
    const targetL = STOP_LIGHTNESS[stop as unknown as keyof typeof STOP_LIGHTNESS];
    const sAdjusted = targetL > 80 ? Math.min(s, 80) : s;
    root.style.setProperty(`--accent-${stop}`, hslToHex(h, sAdjusted, targetL));
  }
}
